import React, { useState, useEffect } from 'react';
import { pendingActionsApi, usageLogApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useProduct } from '../context/ProductContext';
import {
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Zap,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  Trash2,
  Eye,
  FileText,
  Mail,
  Calendar,
  CreditCard,
  Filter,
  CheckSquare,
  Square
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';

export default function PendingActionsPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const { selectedProduct } = useProduct();
  const [queue, setQueue] = useState({ pending: [], generated: [] });
  const [usageSummary, setUsageSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmBulkSend, setConfirmBulkSend] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [processing, setProcessing] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedProduct && selectedProduct !== 'all') params.product_id = selectedProduct;
      const [queueRes, summaryRes] = await Promise.all([
        pendingActionsApi.getQueue(params),
        usageLogApi.getSummary(1)
      ]);
      setQueue(queueRes.data);
      setUsageSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load pending actions');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (actionId) => {
    if (!confirm(`This will generate AI content and charge credits. Continue?`)) {
      return;
    }
    
    setProcessing(actionId);
    try {
      await pendingActionsApi.generate(actionId);
      toast.success('Content generated successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setProcessing(null);
    }
  };

  const handleApprove = async (actionId) => {
    setProcessing(actionId);
    try {
      await pendingActionsApi.approve(actionId);
      toast.success('Action approved');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleSend = async (actionId) => {
    setProcessing(actionId);
    try {
      await pendingActionsApi.send(actionId);
      toast.success('Email sent successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setProcessing(null);
    }
  };

  const handleBulkSend = async () => {
    if (selectedIds.length === 0) return;
    
    setProcessing('bulk');
    try {
      const result = await pendingActionsApi.bulkSend(selectedIds);
      toast.success(`Sent ${result.data.sent} emails (${result.data.failed} failed)`);
      setSelectedIds([]);
      setConfirmBulkSend(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk send failed');
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (actionId) => {
    if (!confirm('Delete this pending action?')) return;
    
    try {
      await pendingActionsApi.delete(actionId);
      toast.success('Action deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleSelect = (actionId) => {
    setSelectedIds(prev => 
      prev.includes(actionId) 
        ? prev.filter(id => id !== actionId)
        : [...prev, actionId]
    );
  };

  const selectAllGenerated = () => {
    const generatedIds = queue.generated.map(a => a.id);
    if (selectedIds.length === generatedIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(generatedIds);
    }
  };

  const getActionTypeIcon = (type) => {
    switch (type) {
      case 'scheduling': return <Calendar className="w-4 h-4" />;
      case 'follow_up': return <Mail className="w-4 h-4" />;
      case 'reminder_24h': return <Clock className="w-4 h-4" />;
      case 'reminder_1h': return <Clock className="w-4 h-4" />;
      case 'proposal_follow_up': return <FileText className="w-4 h-4" />;
      default: return <Zap className="w-4 h-4" />;
    }
  };

  const getActionTypeLabel = (type) => {
    const labels = {
      scheduling: 'Scheduling',
      follow_up: 'Follow-up',
      reminder_24h: '24h Reminder',
      reminder_1h: '1h Reminder',
      proposal_follow_up: 'Proposal Follow-up'
    };
    return labels[type] || type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-64"></div>
          <div className="h-64 bg-slate-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  const allItems = [...queue.pending, ...queue.generated];
  const filteredItems = statusFilter === 'all' 
    ? allItems 
    : allItems.filter(a => a.status === statusFilter);

  return (
    <div className="p-8" data-testid="pending-actions-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Pending Actions Queue</h1>
          <p className="text-slate-500 mt-1">Review and approve automated actions before they're sent</p>
        </div>
        <Button onClick={loadData} variant="outline" className="gap-2" data-testid="refresh-queue-btn">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Generation</p>
                <p className="text-2xl font-bold text-amber-600">{queue.pending_count}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Ready to Send</p>
                <p className="text-2xl font-bold text-emerald-600">{queue.generated_count}</p>
              </div>
              <Send className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Today's Credits</p>
                <p className="text-2xl font-bold">{usageSummary?.today_credits_used || 0}</p>
              </div>
              <CreditCard className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Daily Cap Remaining</p>
                <p className="text-2xl font-bold text-purple-600">{usageSummary?.daily_cap_remaining || 0}</p>
              </div>
              <Zap className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions */}
      {queue.generated_count > 0 && (
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllGenerated}
                  className="gap-2"
                  data-testid="select-all-btn"
                >
                  {selectedIds.length === queue.generated.length ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  Select All Ready ({queue.generated_count})
                </Button>
                {selectedIds.length > 0 && (
                  <span className="text-sm text-slate-500">
                    {selectedIds.length} selected
                  </span>
                )}
              </div>
              {selectedIds.length > 0 && (
                <Button
                  onClick={() => setConfirmBulkSend(true)}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="bulk-send-btn"
                >
                  <Send className="w-4 h-4" />
                  Send Selected ({selectedIds.length})
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4 mb-4">
        <Filter className="w-4 h-4 text-slate-500" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Items ({allItems.length})</SelectItem>
            <SelectItem value="pending">Pending ({queue.pending_count})</SelectItem>
            <SelectItem value="generated">Generated ({queue.generated_count})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Queue List */}
      {filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold">Queue is Empty</h3>
            <p className="text-slate-500">No pending actions require your attention</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((action) => (
            <Card 
              key={action.id} 
              className={`transition-all ${selectedIds.includes(action.id) ? 'ring-2 ring-blue-500' : ''}`}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {action.status === 'generated' && (
                      <button
                        onClick={() => toggleSelect(action.id)}
                        className="p-1 hover:bg-slate-100 rounded"
                        data-testid={`select-${action.id}`}
                      >
                        {selectedIds.includes(action.id) ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                    )}
                    
                    <div className={`p-2 rounded-lg ${
                      action.status === 'pending' ? 'bg-amber-100' : 'bg-emerald-100'
                    }`}>
                      {getActionTypeIcon(action.action_type)}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{action.lead_name || 'Unknown Lead'}</span>
                        <Badge variant={action.status === 'pending' ? 'secondary' : 'default'}>
                          {action.status}
                        </Badge>
                        <Badge variant="outline">
                          {getActionTypeLabel(action.action_type)}
                        </Badge>
                        {action.product_name && (
                          <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                            {action.product_name}
                          </Badge>
                        )}
                        {action.agent_type && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 capitalize">
                            {String(action.agent_type).replace(/_/g, ' ')}
                          </Badge>
                        )}
                        {action.risk_level && (
                          <Badge variant="outline" className={
                            action.risk_level === 'high' ? 'bg-red-50 text-red-700 border-red-200' :
                            action.risk_level === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            'bg-emerald-50 text-emerald-700 border-emerald-200'
                          }>
                            Risk: {action.risk_level}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-slate-500 flex items-center gap-4 mt-1">
                        <span>{action.lead_email}</span>
                        <span>•</span>
                        <span>{action.lead_company}</span>
                        <span>•</span>
                        <span>{formatDate(action.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500 mr-2">
                      {action.status === 'pending' ? `~${action.estimated_credits}` : action.actual_credits_charged} credits
                    </span>

                    {action.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => handleGenerate(action.id)}
                        disabled={processing === action.id}
                        className="gap-1"
                        data-testid={`generate-${action.id}`}
                      >
                        <Zap className="w-4 h-4" />
                        {processing === action.id ? 'Generating...' : 'Generate'}
                      </Button>
                    )}

                    {action.status === 'generated' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedAction(action);
                            setPreviewOpen(true);
                          }}
                          className="gap-1"
                          data-testid={`preview-${action.id}`}
                        >
                          <Eye className="w-4 h-4" />
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSend(action.id)}
                          disabled={processing === action.id}
                          className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`send-${action.id}`}
                        >
                          <Send className="w-4 h-4" />
                          {processing === action.id ? 'Sending...' : 'Send'}
                        </Button>
                      </>
                    )}

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(action.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-${action.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* AI Reason */}
                {action.ai_reason && (
                  <div className="mt-3 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid={`ai-reason-${action.id}`}>
                    <span className="font-medium">AI Reason:</span> {action.ai_reason}
                  </div>
                )}

                {/* Generated Content Preview */}
                {action.status === 'generated' && action.generated_content && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-600 line-clamp-2">
                      {action.generated_content}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              Review the generated content before sending
            </DialogDescription>
          </DialogHeader>
          
          {selectedAction && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">To</p>
                  <p className="font-medium">{selectedAction.lead_name} ({selectedAction.lead_email})</p>
                </div>
              </div>
              
              <div className="p-4 border rounded-lg">
                <p className="whitespace-pre-wrap">{selectedAction.generated_content}</p>
              </div>

              {selectedAction.ai_reason && (
                <div className="p-3 rounded bg-amber-50 border border-amber-200 text-sm text-amber-900">
                  <span className="font-medium">AI Reason:</span> {selectedAction.ai_reason}
                </div>
              )}
              
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <CreditCard className="w-4 h-4" />
                <span>{selectedAction.actual_credits_charged} credits charged</span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                handleSend(selectedAction.id);
                setPreviewOpen(false);
              }}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              <Send className="w-4 h-4" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Send Confirmation Dialog */}
      <Dialog open={confirmBulkSend} onOpenChange={setConfirmBulkSend}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Bulk Send
            </DialogTitle>
            <DialogDescription>
              You are about to send {selectedIds.length} emails. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-amber-800">
              <strong>{selectedIds.length}</strong> emails will be sent immediately to the selected leads.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBulkSend(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkSend}
              disabled={processing === 'bulk'}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-bulk-send"
            >
              <Send className="w-4 h-4" />
              {processing === 'bulk' ? 'Sending...' : `Send ${selectedIds.length} Emails`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
